import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@amzn/meridian/box';
import Card from '@amzn/meridian/card';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Input from '@amzn/meridian/input';
import Link from '@amzn/meridian/link';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import searchTokens from '@amzn/meridian-tokens/base/icon/search';
import { useAppSelector } from '../../store';
import { ROUTES } from '../../utils/constants';
import { selectStationCode } from '../../store/slices/shiftPlanSlice';
import { getImageQualityReport, logImageQualityAction } from '../imageQuality/api';
import { ASSOCIATE_SCORECARDS_PAGE_VIEW } from '../../utils/metric-utils';
import { Cohort, CohortFilter } from '../imageQuality/constants';
import { AssociateImageQuality, GetImageQualityReportOutput } from '../imageQuality/model';
import {
  associateCohort,
  filterByCohort,
  formatCycleTime,
  formatPct,
  passRatePct,
  qualityTier,
} from '../imageQuality/imageQualityUtils';
import { useReportFetch, reportRequestKey } from '../imageQuality/useReportFetch';
import {
  ImageQualityLinkState,
  SHIFT_OPTIONS,
  useReportWindowFilters,
} from '../imageQuality/useImageQualityFilters';
import { RangePresetButtons } from '../imageQuality/ReportWindowBar';
import { LoginLink } from '../imageQuality/AssociateList';
import {
  CohortChips,
  LoadFailed,
  PageTitle,
  StatCard,
  textColorForTier,
} from '../imageQuality/meridianUi';
import { CohortBadge, TogglePill } from './scorecardUi';
import AssociateDrillIn from './AssociateDrillIn';
import {
  ENGAGE_URL,
  SCORECARD_COLUMNS,
  ScorecardColumn,
  ScorecardSortColumn,
  SortDirection,
  searchByLogin,
  sortScorecards,
} from './scorecardUtils';

/** 12.34 → "12.3%"; "—" until the backend sends the rate. */
const formatRate = (pct?: number): string => (pct === undefined ? '—' : formatPct(pct));

/**
 * Associate Scorecards for the Boson-selected site: every associate with a TOP / MID / LOW
 * badge (from their image quality) and the image-quality metrics Retake %, Cycle (s) and Skip %,
 * each of which can be shown or hidden. The login opens that associate on the Image Quality page
 * (a separate page) for the same window; Drill in opens a side drawer; Engage opens People
 * Insight. Other scorecard metrics in the mock (productivity, DPMO, accuracy) have no data source
 * yet.
 */
const AssociateScorecards: React.FC = () => {
  const stationCode = useAppSelector(selectStationCode);
  const filters = useReportWindowFilters();
  const { reportWindow, noteDataAvailableFrom } = filters;
  // Nothing is fetched until Boson has selected a station.
  const warehouseId = stationCode ?? '';
  const {
    data: report,
    loading,
    failed,
    retry,
  } = useReportFetch(
    () => getImageQualityReport({ warehouseId, ...reportWindow }),
    reportRequestKey(reportWindow, warehouseId),
    !stationCode
  );
  const navigate = useNavigate();
  const openImageQuality = (login: string) => {
    const state: ImageQualityLinkState = { associateLogin: login, window: filters.currentWindow };
    navigate(ROUTES.IMAGE_QUALITY, { state });
  };
  const [drillLogin, setDrillLogin] = useState<string | null>(null);
  const [cohort, setCohort] = useState<CohortFilter>('');
  const [query, setQuery] = useState('');
  const [visibleColumns, setVisibleColumns] = useState<Set<ScorecardColumn>>(
    () => new Set(SCORECARD_COLUMNS.map(column => column.key))
  );
  const toggleColumn = (column: ScorecardColumn) =>
    setVisibleColumns(current => {
      const next = new Set(current);
      if (next.has(column)) next.delete(column);
      else next.add(column);
      return next;
    });

  useEffect(() => {
    noteDataAvailableFrom(report?.dataAvailableFrom);
  }, [report, noteDataAvailableFrom]);

  useEffect(() => {
    logImageQualityAction(ASSOCIATE_SCORECARDS_PAGE_VIEW);
  }, []);

  // A drawer or cohort picked for one window or site is meaningless for another. The cohort chips
  // only show when the report lists every associate, so a stale cohort could also hide its own reset.
  useEffect(() => {
    setDrillLogin(null);
    setCohort('');
  }, [reportWindow, stationCode]);

  // Until the backend sends every associate, the scorecards show the bottom share it does send.
  const associates = report ? (report.associates ?? report.lowestAssociates) : [];

  return (
    <Column spacing="500" spacingInset="500" data-testid="scorecards">
      <PageTitle
        title="Associate Scorecards"
        sub={
          stationCode
            ? `Image quality, one view per associate · ${stationCode} · ${filters.windowLabel}`
            : 'Image quality, one view per associate'
        }
        capturedAt={report?.latestCapturedAt}
      />

      <Card spacingInset="none" data-testid="scorecards-filters">
        <Row
          alignmentHorizontal="justify"
          alignmentVertical="center"
          spacingInset="400 500"
          wrap="down"
          spacing="400"
        >
          <Row spacing="300" alignmentVertical="center" wrap="down">
            <Text tag="label" type="h100" color="secondary">
              SHIFT
            </Text>
            <Select
              value={filters.shift}
              onChange={filters.setShift}
              width={150}
              size="small"
              data-testid="iq-shift-select"
            >
              {SHIFT_OPTIONS.map(opt => (
                <SelectOption key={opt.value} value={opt.value} label={opt.label} />
              ))}
            </Select>
            <Text tag="label" type="h100" color="secondary">
              SEARCH
            </Text>
            <Input
              value={query}
              onChange={setQuery}
              placeholder="Search login..."
              prefixIconTokens={searchTokens}
              width={240}
              size="small"
              aria-label="Search associates"
              data-testid="scorecards-search"
            />
          </Row>
          <RangePresetButtons filters={filters} />
        </Row>
        {report && (
          <>
            <Divider />
            <Box spacingInset="300 500">
              {report.associates ? (
                <CohortChips
                  associates={associates}
                  thresholds={report.thresholds}
                  value={cohort}
                  onChange={setCohort}
                />
              ) : (
                <Text type="b300" color="secondary">
                  {`Bottom ${report.thresholds.lowestPercent}% of ${(report.summary.rankedAssociateCount ?? report.summary.associateCount).toLocaleString()} ranked associates`}
                </Text>
              )}
            </Box>
          </>
        )}
        <Divider />
        <Row
          spacing="300"
          alignmentVertical="center"
          spacingInset="300 500"
          wrap="down"
          data-testid="scorecards-columns"
        >
          <Text type="h100" color="secondary">
            COLUMNS:
          </Text>
          {SCORECARD_COLUMNS.map(column => (
            <TogglePill
              key={column.key}
              label={column.label}
              selected={visibleColumns.has(column.key)}
              onToggle={() => toggleColumn(column.key)}
              testId={`scorecards-column-${column.key}`}
            />
          ))}
        </Row>
      </Card>

      {!stationCode && (
        <Card spacingInset="500">
          <Text color="secondary">Select a site in RCC to view associate scorecards.</Text>
        </Card>
      )}
      {loading && (
        <Row alignmentHorizontal="center" spacingInset="500">
          <Loader size="large" />
        </Row>
      )}
      {failed && (
        <LoadFailed
          message="Couldn't load the scorecards."
          onRetry={retry}
          testId="scorecards-failed"
        />
      )}
      {stationCode && !loading && report && (
        <>
          <ScorecardKpis report={report} associates={associates} />
          <ScorecardTable
            report={report}
            associates={searchByLogin(filterByCohort(associates, report.thresholds, cohort), query)}
            totalCount={associates.length}
            visibleColumns={visibleColumns}
            onOpenImageQuality={openImageQuality}
            onDrillIn={setDrillLogin}
          />
        </>
      )}
      {stationCode && (
        <AssociateDrillIn
          warehouseId={stationCode}
          reportWindow={reportWindow}
          login={drillLogin}
          onClose={() => setDrillLogin(null)}
          onOpenImageQuality={openImageQuality}
        />
      )}
    </Column>
  );
};

const ScorecardKpis: React.FC<{
  report: GetImageQualityReportOutput;
  associates: AssociateImageQuality[];
}> = ({ report, associates }) => {
  const { thresholds, summary } = report;
  const bottomCount = associates.filter(
    associate => associateCohort(associate, thresholds) === Cohort.BOTTOM
  ).length;
  const sitePct = passRatePct(summary.passingPhotos, summary.totalPhotos);
  const siteTier = qualityTier(sitePct, thresholds);
  return (
    <Row spacing="400" widths="fill" alignmentVertical="stretch" data-testid="scorecards-kpis">
      <StatCard
        label="Active associates"
        hero={summary.associateCount.toLocaleString()}
        sub="captured photos in this range"
      />
      <StatCard
        label="Image quality"
        hero={formatPct(sitePct)}
        heroColor={textColorForTier(siteTier)}
        sub="site photos passing all three checks"
      />
      <StatCard
        label="Image alerts"
        hero={bottomCount.toLocaleString()}
        heroColor={bottomCount > 0 ? 'error' : 'success'}
        sub={bottomCount > 0 ? `associates below ${thresholds.floorPassRatePct}%` : 'all clear'}
        testId="scorecards-alerts"
      />
      <StatCard
        label={`Below ${thresholds.floorPassRatePct}% repeatedly`}
        hero={summary.repeatBelowFloorCount.toLocaleString()}
        sub={`in ${thresholds.repeatMinWeeks}+ of the last ${thresholds.repeatWindowWeeks} weeks`}
      />
    </Row>
  );
};

const ScorecardTable: React.FC<{
  report: GetImageQualityReportOutput;
  /** Already filtered by cohort and search. */
  associates: AssociateImageQuality[];
  totalCount: number;
  visibleColumns: Set<ScorecardColumn>;
  onOpenImageQuality: (login: string) => void;
  onDrillIn: (login: string) => void;
}> = ({ report, associates, totalCount, visibleColumns, onOpenImageQuality, onDrillIn }) => {
  const { thresholds } = report;
  // Unsorted = the backend's order (worst image quality first).
  const [sortColumn, setSortColumn] = useState<ScorecardSortColumn | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<SortDirection>('ascending');
  const rows = useMemo(
    () => sortScorecards(associates, sortColumn, sortDirection),
    [associates, sortColumn, sortDirection]
  );
  const shows = (column: ScorecardColumn) => visibleColumns.has(column);

  return (
    <Card spacingInset="none">
      <Table
        headerRows={1}
        showDividers
        spacing="medium"
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={({
          sortColumn: column,
          sortDirection: direction,
        }: {
          sortColumn: ScorecardSortColumn;
          sortDirection: SortDirection;
        }) => {
          setSortColumn(column);
          setSortDirection(direction);
        }}
        data-testid="scorecards-table"
      >
        <TableRow>
          <TableCell header sortColumn="login">
            ASSOCIATE
          </TableCell>
          {shows(ScorecardColumn.RETAKE_RATE) && (
            <TableCell header sortColumn="retakeRate" width={140} alignmentHorizontal="end">
              RETAKE %
            </TableCell>
          )}
          {shows(ScorecardColumn.CYCLE_TIME) && (
            <TableCell header sortColumn="cycleTime" width={140} alignmentHorizontal="end">
              CYCLE (S)
            </TableCell>
          )}
          {shows(ScorecardColumn.SKIP_RATE) && (
            <TableCell header sortColumn="skipRate" width={140} alignmentHorizontal="end">
              SKIP %
            </TableCell>
          )}
          <TableCell header width={150}>
            ACTIONS
          </TableCell>
        </TableRow>
        {rows.map(associate => {
          const cohort = associateCohort(associate, thresholds);
          return (
            <TableRow
              key={associate.login}
              highlightOnHover
              data-testid={`scorecard-${associate.login}`}
            >
              <TableCell>
                <Row spacing="300" alignmentVertical="center">
                  <LoginLink login={associate.login} onSelect={onOpenImageQuality} />
                  {cohort && <CohortBadge cohort={cohort} />}
                </Row>
              </TableCell>
              {shows(ScorecardColumn.RETAKE_RATE) && (
                <TableCell alignmentHorizontal="end">
                  <Text>{formatRate(associate.retakeRatePct)}</Text>
                </TableCell>
              )}
              {shows(ScorecardColumn.CYCLE_TIME) && (
                <TableCell alignmentHorizontal="end">
                  <Text>{formatCycleTime(associate.avgCycleTimeSeconds)}</Text>
                </TableCell>
              )}
              {shows(ScorecardColumn.SKIP_RATE) && (
                <TableCell alignmentHorizontal="end">
                  <Text>{formatRate(associate.skipRatePct)}</Text>
                </TableCell>
              )}
              <TableCell>
                <Row spacing="400">
                  <Link
                    onClick={() => onDrillIn(associate.login)}
                    data-testid={`scorecard-${associate.login}-drill`}
                  >
                    Drill in
                  </Link>
                  <Link
                    href={ENGAGE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`scorecard-${associate.login}-engage`}
                  >
                    Engage
                  </Link>
                </Row>
              </TableCell>
            </TableRow>
          );
        })}
      </Table>
      {rows.length === 0 && (
        <Box spacingInset="500">
          <Text color="secondary">No associates match.</Text>
        </Box>
      )}
      <Box spacingInset="300 500">
        <Text
          type="b200"
          color="secondary"
        >{`Showing ${rows.length} of ${totalCount} associates`}</Text>
      </Box>
    </Card>
  );
};

export default AssociateScorecards;
